import React, { useEffect, useRef } from 'react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';

import ProfileEditInput from 'app/components/CommonStyled/ProfileEditInput';
import { Form, Formik, FormikProps } from 'formik';
import { postOutsideWork } from 'services/profile-editor';
import {
  profileDataSelector,
  setBio,
  setOutsideWork,
} from 'store/reducers/profile-editor/profile-editor-reducer';
import * as Yup from 'yup';

import ProfileEditorDrawer, { ProfileEditorDrawerRefProps } from '../Drawer';
import { ProfileEditorDrawerTitle } from '../styled';
import { ProfileEditorDrawerOutsideWorkWrapper } from './styled';

interface ProfileEditorDrawerOutsideWorkProps {
  onClose: () => void;
}

export default function ProfileEditorDrawerOutsideWork({
  onClose,
}: ProfileEditorDrawerOutsideWorkProps) {
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<any>>(null);
  const drawerRef = useRef<ProfileEditorDrawerRefProps>(null);
  const { outside_work } = useSelector(profileDataSelector);

  const handlePostOutsideWork = async (params: any) => {
    const response = await postOutsideWork(params);
    if (!response.ok) {
      throw new Error(response.originalError?.message);
    }

    dispatch(setOutsideWork(params.value));

    return response.data;
  };

  const { mutate } = useMutation(handlePostOutsideWork, drawerRef.current?.handleMutation);

  const outsideWorkValidationSchema = Yup.object().shape({
    outside_work: Yup.string().max(
      500,
      ({ value }) =>
        `${value.length - 500} ${
          value.length - 500 !== 1 ? 'characters' : 'character'
        } over the limit. Update your "outside work" to fit within the limit to publish it to your profile!`
    ),
  });

  const handleSubmit = (values) => {
    mutate({ value: values.outside_work });
  };

  return (
    <Formik
      initialValues={{
        outside_work,
      }}
      validationSchema={outsideWorkValidationSchema}
      onSubmit={handleSubmit}
      innerRef={formikRef}
    >
      {(props) => (
        <Form>
          <ProfileEditorDrawer
            ref={drawerRef}
            header={<ProfileEditorDrawerOutsideWorkHeader />}
            needsSaving={props.dirty}
            onClose={onClose}
          >
            <ProfileEditorDrawerOutsideWorkWrapper>
              <ProfileEditInput
                {...props}
                name={'outside_work'}
                label={
                  'What interests, projects or side hustles (as they say in North America), do you spend time on?'
                }
                tooltip={
                  'Any activities, outside of your role, like projects, community work or hobbies can add a lot of texture to your profile. Add only what you feel comfortable sharing.'
                }
                type={'text'}
                placeholder={`When I'm not glued to my computer, you can find me working in making surreal and serene ceramic sculptures...`}
                textLimit={500}
              />
            </ProfileEditorDrawerOutsideWorkWrapper>
          </ProfileEditorDrawer>
        </Form>
      )}
    </Formik>
  );
}

function ProfileEditorDrawerOutsideWorkHeader() {
  return <ProfileEditorDrawerTitle>Outside of Work</ProfileEditorDrawerTitle>;
}
